import { createClient } from '@supabase/supabase-js';

// Replace these with your actual Supabase project credentials
// You can find these in your Supabase dashboard under Settings > API
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'YOUR_SUPABASE_URL';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || 'YOUR_SUPABASE_ANON_KEY';

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'codebreakers-auth',
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
});

// Export storage helper
export const storage = supabase.storage;

// Auth helper functions
export const authHelpers = {
  // Sign up new user
  async signUp(email, password, metadata = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata // { name, cohort_id, role }
      }
    });
    return { data, error };
  },

  // Sign in existing user
  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  },

  // Sign out
  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  // Get current session
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  // Get current user
  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  // Listen to auth state changes
  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  },

  // Send password reset email
  async resetPassword(email) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/`
    });
    return { data, error };
  },

  // Update password (after clicking reset link)
  async updatePassword(newPassword) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    });
    return { data, error };
  },

  // Admin: Invite user by email (sends invitation email, user sets own password)
  async adminInviteUser({ email, name, role, cohortId }) {
    // Try to get current session, refresh if stale
    let { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      // Attempt to refresh the session
      const { data: refreshData } = await supabase.auth.refreshSession();
      session = refreshData?.session;
    }
    if (!session?.access_token) {
      return { data: null, error: { message: 'Not authenticated. Please log out and log back in.' } };
    }

    try {
      const response = await fetch('/.netlify/functions/admin-create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ email, name, role, cohortId, method: 'invite' }),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: { message: result.error || 'Failed to invite user' } };
      }
      return { data: result, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message || 'Network error' } };
    }
  },

  // Admin: Manually create user with temporary password
  async adminCreateUser({ email, name, role, cohortId }) {
    // Try to get current session, refresh if stale
    let { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      session = refreshData?.session;
    }
    if (!session?.access_token) {
      return { data: null, error: { message: 'Not authenticated. Please log out and log back in.' } };
    }

    try {
      const response = await fetch('/.netlify/functions/admin-create-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ email, name, role, cohortId, method: 'create' }),
      });

      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: { message: result.error || 'Failed to create user' } };
      }
      return { data: result, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message || 'Network error' } };
    }
  },

  // Admin: Fetch the full Thinkific progress payload for a single student
  // (or a single unlinked Thinkific enrollment). Returns the data needed
  // to render the three Thinkific tabs (Enrollment, Activity feed, Quiz &
  // Survey) into a PDF. The frontend invokes this once per student and
  // throttles the calls so each invocation stays inside Netlify's budget.
  async thinkificReport({ studentId, thinkificEnrollmentId, diagnose } = {}) {
    let { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      session = refreshData?.session;
    }
    if (!session?.access_token) {
      return { data: null, error: { message: 'Not authenticated. Please log out and log back in.' } };
    }

    try {
      const response = await fetch('/.netlify/functions/thinkific-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ studentId, thinkificEnrollmentId, diagnose: !!diagnose }),
      });
      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: { message: result.error || 'Report fetch failed' } };
      }
      return { data: result, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message || 'Network error' } };
    }
  },

  // Admin: Thinkific NCLEX course enrollment proxy.
  // All actions go through the same Netlify function which validates the
  // caller is an admin and never accepts a course_id from the client.
  async thinkificAction(action, payload = {}) {
    let { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      session = refreshData?.session;
    }
    if (!session?.access_token) {
      return { data: null, error: { message: 'Not authenticated. Please log out and log back in.' } };
    }

    try {
      const response = await fetch('/.netlify/functions/thinkific-enroll', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ action, ...payload }),
      });
      const result = await response.json();
      if (!response.ok) {
        return { data: null, error: { message: result.error || 'Thinkific request failed' } };
      }
      return { data: result, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message || 'Network error' } };
    }
  },

  // Admin: Trigger the historical paid-status backfill. Runs as a
  // Netlify Background Function (returns 202 immediately; processing
  // continues for up to 15 minutes). Watch thinkific_audit_log for
  // entries with metadata.kind = 'paid_backfill' to track progress.
  async thinkificPaidBackfill(opts = {}) {
    let { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      session = refreshData?.session;
    }
    if (!session?.access_token) {
      return { data: null, error: { message: 'Not authenticated. Please log out and log back in.' } };
    }
    try {
      const response = await fetch('/.netlify/functions/thinkific-paid-backfill-background', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(opts),
      });
      // Background functions reply 202 immediately. The fetch resolves;
      // the body may or may not parse depending on Netlify's wrapper.
      let result = null;
      try { result = await response.json(); } catch { /* empty body is fine */ }
      if (!response.ok && response.status !== 202) {
        return { data: null, error: { message: result?.error || 'Backfill request failed' } };
      }
      return { data: result || { ok: true, status: response.status }, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message || 'Network error' } };
    }
  },

  // Admin: Permanently delete a user (auth + profile)
  async adminDeleteUser(userId) {
    let { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      const { data: refreshData } = await supabase.auth.refreshSession();
      session = refreshData?.session;
    }
    if (!session?.access_token) {
      return { error: { message: 'Not authenticated. Please log out and log back in.' } };
    }

    try {
      const response = await fetch('/.netlify/functions/admin-delete-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ userId }),
      });

      const result = await response.json();
      if (!response.ok) {
        return { error: { message: result.error || 'Failed to delete user' } };
      }
      return { error: null };
    } catch (err) {
      return { error: { message: err.message || 'Network error' } };
    }
  }
};

// Database helper functions
export const db = {
  // =====================================================
  // PROFILES
  // =====================================================
  async getProfile(userId) {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single();
    return { data, error };
  },

  async updateProfile(userId, updates) {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();
    return { data, error };
  },

  async getAllProfiles() {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // =====================================================
  // COHORTS
  // =====================================================
  async getCohorts() {
    const { data, error } = await supabase
      .from('cohorts')
      .select('*')
      .order('start_date', { ascending: true });
    return { data, error };
  },

  async getCohort(cohortId) {
    const { data, error } = await supabase
      .from('cohorts')
      .select('*')
      .eq('id', cohortId)
      .single();
    return { data, error };
  },

  async createCohort(cohortData) {
    const { data, error } = await supabase
      .from('cohorts')
      .insert(cohortData)
      .select()
      .single();
    return { data, error };
  },

  async updateCohort(cohortId, updates) {
    const { data, error } = await supabase
      .from('cohorts')
      .update(updates)
      .eq('id', cohortId)
      .select()
      .single();
    return { data, error };
  },

  // =====================================================
  // MODULES
  // =====================================================
  async getModules() {
    const { data, error } = await supabase
      .from('modules')
      .select(`
        *,
        module_items (*)
      `)
      .order('week_number', { ascending: true });
    return { data, error };
  },

  async getModuleWithItems(moduleId) {
    const { data, error } = await supabase
      .from('modules')
      .select(`
        *,
        module_items (*)
      `)
      .eq('id', moduleId)
      .order('module_items(item_order)', { ascending: true })
      .single();
    return { data, error };
  },

  // =====================================================
  // MODULE ITEMS
  // =====================================================
  async getModuleItems(moduleId) {
    const { data, error } = await supabase
      .from('module_items')
      .select('*')
      .eq('module_id', moduleId)
      .order('item_order', { ascending: true });
    return { data, error };
  },

  async createModuleItem(itemData) {
    const { data, error } = await supabase
      .from('module_items')
      .insert(itemData)
      .select()
      .single();
    return { data, error };
  },

  async updateModuleItem(itemId, updates) {
    const { data, error } = await supabase
      .from('module_items')
      .update(updates)
      .eq('id', itemId)
      .select()
      .single();
    return { data, error };
  },

  async deleteModuleItem(itemId) {
    const { error } = await supabase
      .from('module_items')
      .delete()
      .eq('id', itemId);
    return { error };
  },

  // =====================================================
  // STUDENT PROGRESS (The key functions!)
  // =====================================================
  
  // Get all progress for a user
  async getUserProgress(userId) {
    const { data, error } = await supabase
      .from('student_progress')
      .select('*')
      .eq('user_id', userId);
    return { data, error };
  },

  // Mark an item as completed
  async markItemCompleted(userId, moduleItemId) {
    const { data, error } = await supabase
      .from('student_progress')
      .upsert({
        user_id: userId,
        module_item_id: moduleItemId,
        completed: true,
        completed_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,module_item_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // Mark an item as not completed (if needed)
  async markItemIncomplete(userId, moduleItemId) {
    const { data, error } = await supabase
      .from('student_progress')
      .upsert({
        user_id: userId,
        module_item_id: moduleItemId,
        completed: false,
        completed_at: null
      }, {
        onConflict: 'user_id,module_item_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // Track that a student viewed an item (clicked into it)
  async trackItemViewed(userId, moduleItemId) {
    // First check if record exists
    const { data: existing } = await supabase
      .from('student_progress')
      .select('*')
      .eq('user_id', userId)
      .eq('module_item_id', moduleItemId)
      .single();

    if (existing) {
      // Only update viewed_at if not already set
      if (!existing.viewed_at) {
        const { data, error } = await supabase
          .from('student_progress')
          .update({ viewed_at: new Date().toISOString() })
          .eq('user_id', userId)
          .eq('module_item_id', moduleItemId)
          .select()
          .single();
        return { data, error };
      }
      return { data: existing, error: null };
    } else {
      // Create new record with viewed_at
      const { data, error } = await supabase
        .from('student_progress')
        .insert({
          user_id: userId,
          module_item_id: moduleItemId,
          viewed_at: new Date().toISOString(),
          completed: false
        })
        .select()
        .single();
      return { data, error };
    }
  },

  // Get progress summary using the database function
  async getUserProgressSummary(userId) {
    const { data, error } = await supabase
      .rpc('get_user_progress', { p_user_id: userId });
    return { data, error };
  },

  // Check if a specific module is complete
  async getModuleCompletion(userId, moduleId) {
    const { data, error } = await supabase
      .rpc('get_module_completion', { 
        p_user_id: userId, 
        p_module_id: moduleId 
      });
    return { data, error };
  },

  // =====================================================
  // COHORT MODULE VISIBILITY
  // =====================================================
  async getCohortVisibility(cohortId) {
    const { data, error } = await supabase
      .from('cohort_module_visibility')
      .select('*')
      .eq('cohort_id', cohortId);
    return { data, error };
  },

  async updateCohortVisibility(cohortId, moduleId, visibility) {
    const { data, error } = await supabase
      .from('cohort_module_visibility')
      .upsert({
        cohort_id: cohortId,
        module_id: moduleId,
        ...visibility
      }, {
        onConflict: 'cohort_id,module_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // =====================================================
  // SYLLABUS PDFs
  // =====================================================
  
  // Get cohort-specific syllabus PDFs
  async getSyllabusPdfs(cohortId) {
    const { data, error } = await supabase
      .from('syllabus_pdfs')
      .select('*')
      .eq('cohort_id', cohortId)
      .order('uploaded_at', { ascending: false });
    return { data, error };
  },

  // Upload cohort-specific syllabus PDF
  async uploadSyllabusPdf(cohortId, pdfData) {
    const { data, error } = await supabase
      .from('syllabus_pdfs')
      .insert({ cohort_id: cohortId, ...pdfData })
      .select()
      .single();
    return { data, error };
  },

  async deleteSyllabusPdf(pdfId) {
    const { error } = await supabase
      .from('syllabus_pdfs')
      .delete()
      .eq('id', pdfId);
    return { error };
  },

  // =====================================================
  // MASTER SYLLABUS PDFs (visible to ALL cohorts)
  // =====================================================
  
  // Get master syllabus PDFs (cohort_id IS NULL)
  async getMasterSyllabusPdfs() {
    const { data, error } = await supabase
      .from('syllabus_pdfs')
      .select('*')
      .is('cohort_id', null)
      .order('uploaded_at', { ascending: false });
    return { data, error };
  },

  // Upload master syllabus PDF (no cohort_id)
  async uploadMasterSyllabusPdf(pdfData) {
    const { data, error } = await supabase
      .from('syllabus_pdfs')
      .insert({ cohort_id: null, ...pdfData })
      .select()
      .single();
    return { data, error };
  },

  // =====================================================
  // CALENDAR EVENTS
  // =====================================================
  async getCalendarEvents() {
    const { data, error } = await supabase
      .from('calendar_events')
      .select('*')
      .order('week_number', { ascending: true });
    return { data, error };
  },

  async getCohortCustomEvents(cohortId) {
    const { data, error } = await supabase
      .from('cohort_custom_events')
      .select('*')
      .eq('cohort_id', cohortId)
      .order('event_date', { ascending: true });
    return { data, error };
  },

  async createCohortEvent(eventData) {
    const { data, error } = await supabase
      .from('cohort_custom_events')
      .insert(eventData)
      .select()
      .single();
    return { data, error };
  },

  async deleteCohortEvent(eventId) {
    const { error } = await supabase
      .from('cohort_custom_events')
      .delete()
      .eq('id', eventId);
    return { error };
  },

  // =====================================================
  // EXTERNAL RESOURCES
  // =====================================================
  async getExternalResources() {
    const { data, error } = await supabase
      .from('external_resources')
      .select('*')
      .eq('is_active', true)
      .order('display_order', { ascending: true });
    return { data, error };
  },

  async createExternalResource(resourceData) {
    const { data, error } = await supabase
      .from('external_resources')
      .insert(resourceData)
      .select()
      .single();
    return { data, error };
  },

  async updateExternalResource(resourceId, updates) {
    const { data, error } = await supabase
      .from('external_resources')
      .update(updates)
      .eq('id', resourceId)
      .select()
      .single();
    return { data, error };
  },

  async deleteExternalResource(resourceId) {
    const { error } = await supabase
      .from('external_resources')
      .delete()
      .eq('id', resourceId);
    return { error };
  },

  // =====================================================
  // COHORT ITEM OVERRIDES (for cohort-specific scheduling)
  // =====================================================
  
  // Get all overrides for a cohort
  async getCohortItemOverrides(cohortId) {
    const { data, error } = await supabase
      .from('cohort_item_overrides')
      .select('*')
      .eq('cohort_id', cohortId);
    return { data, error };
  },

  // Create or update an override (upsert)
  async upsertCohortItemOverride(cohortId, moduleItemId, overrideData) {
    // First, fetch any existing override so we don't clobber fields
    const { data: existing } = await supabase
      .from('cohort_item_overrides')
      .select('*')
      .eq('cohort_id', cohortId)
      .eq('module_item_id', moduleItemId)
      .maybeSingle();

    const payload = {
      cohort_id: cohortId,
      module_item_id: moduleItemId,
      updated_at: new Date().toISOString()
    };
    
    // Carry forward existing fields so the upsert doesn't null them out
    if (existing) {
      if (existing.due_day != null) payload.due_day = existing.due_day;
      if (existing.item_order != null) payload.item_order = existing.item_order;
      if (existing.due_date != null) payload.due_date = existing.due_date;
    }
    
    // Then apply the new values (overwriting the carried-forward ones)
    if (overrideData.dueDay !== undefined) {
      payload.due_day = overrideData.dueDay;
    }
    if (overrideData.itemOrder !== undefined) {
      payload.item_order = overrideData.itemOrder;
    }
    if (overrideData.dueDate !== undefined) {
      payload.due_date = overrideData.dueDate;
    }

    const { data, error } = await supabase
      .from('cohort_item_overrides')
      .upsert(payload, {
        onConflict: 'cohort_id,module_item_id'
      })
      .select()
      .single();
    return { data, error };
  },

  // Delete an override (revert to master)
  async deleteCohortItemOverride(cohortId, moduleItemId) {
    const { error } = await supabase
      .from('cohort_item_overrides')
      .delete()
      .eq('cohort_id', cohortId)
      .eq('module_item_id', moduleItemId);
    return { error };
  },

  // =====================================================
  // STORAGE HELPERS (for PDF uploads)
  // =====================================================
  // Check if bucket exists and is accessible
  async checkBucket() {
    try {
      const { data, error } = await supabase.storage.getBucket('syllabus-pdfs');
      if (error) {
        console.error('Bucket check error:', error);
        return { exists: false, error };
      }
      return { exists: true, data };
    } catch (err) {
      console.error('Bucket check exception:', err);
      return { exists: false, error: err };
    }
  },

  // Upload a PDF file to Supabase Storage (for cohort-specific PDFs)
  async uploadPdf(cohortId, file) {
    console.log('uploadPdf called with:', { cohortId, fileName: file.name, fileSize: file.size });
    
    // Sanitize filename - remove special characters
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${cohortId}/${Date.now()}-${sanitizedName}`;
    
    console.log('Uploading to path:', fileName);
    
    try {
      // First check if bucket exists
      const bucketCheck = await this.checkBucket();
      if (!bucketCheck.exists) {
        console.error('Bucket does not exist or is not accessible');
        return { 
          data: null, 
          error: { 
            message: 'Storage bucket "syllabus-pdfs" not found. Please create it in Supabase Dashboard > Storage.' 
          } 
        };
      }

      const { data, error } = await supabase.storage
        .from('syllabus-pdfs')
        .upload(fileName, file, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: false
        });
      
      console.log('Upload response:', { data, error });
      
      if (error) {
        console.error('Storage upload error:', error);
        
        // Provide more helpful error messages
        let errorMessage = error.message;
        if (error.statusCode === 400) {
          errorMessage = 'Upload failed: Check that the storage bucket exists and has proper permissions.';
        } else if (error.statusCode === 403) {
          errorMessage = 'Permission denied: Storage bucket policies may not allow uploads.';
        } else if (error.statusCode === 404) {
          errorMessage = 'Storage bucket "syllabus-pdfs" not found. Please create it in Supabase.';
        }
        
        return { data: null, error: { ...error, message: errorMessage } };
      }
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('syllabus-pdfs')
        .getPublicUrl(fileName);
      
      const publicUrl = urlData?.publicUrl;
      console.log('Public URL:', publicUrl);
      
      return { 
        data: { 
          path: data.path, 
          url: publicUrl,
          filename: file.name,
          size: formatFileSize(file.size)
        }, 
        error: null 
      };
    } catch (err) {
      console.error('Upload exception:', err);
      return { data: null, error: { message: err.message || 'Upload failed unexpectedly' } };
    }
  },

  // Upload a master PDF (stored in 'master/' folder)
  async uploadMasterPdf(file) {
    console.log('uploadMasterPdf called with:', { fileName: file.name, fileSize: file.size });
    
    // Sanitize filename - remove special characters
    const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `master/${Date.now()}-${sanitizedName}`;
    
    console.log('Uploading master PDF to path:', fileName);
    
    try {
      // First check if bucket exists
      const bucketCheck = await this.checkBucket();
      if (!bucketCheck.exists) {
        console.error('Bucket does not exist or is not accessible');
        return { 
          data: null, 
          error: { 
            message: 'Storage bucket "syllabus-pdfs" not found. Please create it in Supabase Dashboard > Storage.' 
          } 
        };
      }

      const { data, error } = await supabase.storage
        .from('syllabus-pdfs')
        .upload(fileName, file, {
          contentType: 'application/pdf',
          cacheControl: '3600',
          upsert: false
        });
      
      if (error) {
        console.error('Storage upload error:', error);
        return { data: null, error };
      }
      
      // Get public URL
      const { data: urlData } = supabase.storage
        .from('syllabus-pdfs')
        .getPublicUrl(fileName);
      
      const publicUrl = urlData?.publicUrl;
      
      return { 
        data: { 
          path: data.path, 
          url: publicUrl,
          filename: file.name,
          size: formatFileSize(file.size)
        }, 
        error: null 
      };
    } catch (err) {
      console.error('Upload exception:', err);
      return { data: null, error: { message: err.message || 'Upload failed unexpectedly' } };
    }
  },

  // Delete a PDF from storage
  async deletePdf(filePath) {
    try {
      const { error } = await supabase.storage
        .from('syllabus-pdfs')
        .remove([filePath]);
      return { error };
    } catch (err) {
      console.error('Delete error:', err);
      return { error: err };
    }
  },

  // Get download URL for a PDF
  getPublicUrl(filePath) {
    const { data } = supabase.storage
      .from('syllabus-pdfs')
      .getPublicUrl(filePath);
    return data.publicUrl;
  },

  // =========================================================================
  // STUDENT NOTES FUNCTIONS
  // =========================================================================

  // Get all notes for a student
  async getStudentNotes(studentId) {
    const { data, error } = await supabase
      .from('student_notes')
      .select('*, admin:admin_id(name, email)')
      .eq('student_id', studentId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Get notes for all students in a cohort
  async getCohortStudentNotes(cohortId) {
    const { data, error } = await supabase
      .from('student_notes')
      .select(`
        *,
        student:student_id(id, name, email, cohort_id),
        admin:admin_id(name)
      `)
      .order('created_at', { ascending: false });
    
    // Filter to only students in the specified cohort
    if (data && cohortId) {
      const filtered = data.filter(note => note.student?.cohort_id === cohortId);
      return { data: filtered, error };
    }
    return { data, error };
  },

  // Create a new student note
  async createStudentNote(noteData) {
    const { data, error } = await supabase
      .from('student_notes')
      .insert({
        student_id: noteData.studentId,
        admin_id: noteData.adminId,
        note_type: noteData.noteType || 'meeting',
        title: noteData.title || null,
        content: noteData.content,
        meeting_date: noteData.meetingDate || null
      })
      .select()
      .single();
    return { data, error };
  },

  // Update a student note
  async updateStudentNote(noteId, noteData) {
    const { data, error } = await supabase
      .from('student_notes')
      .update({
        note_type: noteData.noteType,
        title: noteData.title,
        content: noteData.content,
        meeting_date: noteData.meetingDate
      })
      .eq('id', noteId)
      .select()
      .single();
    return { data, error };
  },

  // Delete a student note
  async deleteStudentNote(noteId) {
    const { error } = await supabase
      .from('student_notes')
      .delete()
      .eq('id', noteId);
    return { error };
  },

  // =========================================================================
  // STUDENT PERCENTILES FUNCTIONS
  // =========================================================================

  // Get all percentiles for a student
  async getStudentPercentiles(studentId) {
    const { data, error } = await supabase
      .from('student_percentiles')
      .select('*')
      .eq('student_id', studentId)
      .order('week_number', { ascending: true });
    return { data, error };
  },

  // Get percentiles for all students in a cohort
  async getCohortPercentiles(cohortId) {
    // First get all students in the cohort
    const { data: students } = await supabase
      .from('profiles')
      .select('id')
      .eq('cohort_id', cohortId)
      .eq('role', 'student');
    
    if (!students || students.length === 0) {
      return { data: [], error: null };
    }

    const studentIds = students.map(s => s.id);
    
    const { data, error } = await supabase
      .from('student_percentiles')
      .select('*')
      .in('student_id', studentIds)
      .order('week_number', { ascending: true });
    
    return { data, error };
  },

  // Create or update a percentile record (upsert)
  async upsertStudentPercentile(percentileData) {
    const { data, error } = await supabase
      .from('student_percentiles')
      .upsert({
        student_id: percentileData.studentId,
        week_number: percentileData.weekNumber,
        percentile: percentileData.percentile,
        questions_completed: percentileData.questionsCompleted || 0,
        source: percentileData.source || 'uworld',
        notes: percentileData.notes || null,
        recorded_by: percentileData.recordedBy
      }, {
        onConflict: 'student_id,week_number,source'
      })
      .select()
      .single();
    return { data, error };
  },

  // Delete a percentile record
  async deleteStudentPercentile(percentileId) {
    const { error } = await supabase
      .from('student_percentiles')
      .delete()
      .eq('id', percentileId);
    return { error };
  },

  // =========================================================================
  // ASSIGNMENT SUBMISSIONS FUNCTIONS
  // =========================================================================

  // Get submission for a specific student and assignment
  async getSubmission(studentId, moduleItemId) {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .select('*')
      .eq('student_id', studentId)
      .eq('module_item_id', moduleItemId)
      .single();
    return { data, error };
  },

  // Get all submissions for a student
  async getStudentSubmissions(studentId) {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .select(`
        *,
        module_item:module_item_id(id, title, type, points, module_id)
      `)
      .eq('student_id', studentId)
      .order('submitted_at', { ascending: false });
    return { data, error };
  },

  // Get all submissions for a module item (admin view)
  async getItemSubmissions(moduleItemId) {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .select(`
        *,
        student:student_id(id, name, email, cohort_id)
      `)
      .eq('module_item_id', moduleItemId)
      .order('submitted_at', { ascending: false });
    return { data, error };
  },

  // Count submissions attached to a module item, without pulling the rows.
  // Used as a safety check before deleting an item.
  async countItemSubmissions(moduleItemId) {
    const { count, error } = await supabase
      .from('assignment_submissions')
      .select('id', { count: 'exact', head: true })
      .eq('module_item_id', moduleItemId);
    return { count: count || 0, error };
  },

  // Get all submissions for a cohort (admin grid view)
  async getCohortSubmissions(cohortId) {
    // First get all students in the cohort
    const { data: students } = await supabase
      .from('profiles')
      .select('id')
      .eq('cohort_id', cohortId)
      .eq('role', 'student');
    
    if (!students || students.length === 0) {
      return { data: [], error: null };
    }

    const studentIds = students.map(s => s.id);
    
    const { data, error } = await supabase
      .from('assignment_submissions')
      .select(`
        *,
        student:student_id(id, name, email),
        module_item:module_item_id(id, title, type, points, module_id)
      `)
      .in('student_id', studentIds)
      .order('submitted_at', { ascending: false });
    
    return { data, error };
  },

  // Create a new submission
  async createSubmission(submissionData) {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .insert({
        student_id: submissionData.studentId,
        module_item_id: submissionData.moduleItemId,
        submission_type: submissionData.submissionType,
        text_content: submissionData.textContent || null,
        file_url: submissionData.fileUrl || null,
        file_name: submissionData.fileName || null,
        file_size: submissionData.fileSize || null,
        file_type: submissionData.fileType || null,
        link_url: submissionData.linkUrl || null,
        status: submissionData.status || 'submitted',
        is_late: submissionData.isLate || false
      })
      .select()
      .single();
    return { data, error };
  },

  // Update a submission (student resubmit)
  async updateSubmission(submissionId, submissionData) {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .update({
        submission_type: submissionData.submissionType,
        text_content: submissionData.textContent || null,
        file_url: submissionData.fileUrl || null,
        file_name: submissionData.fileName || null,
        file_size: submissionData.fileSize || null,
        file_type: submissionData.fileType || null,
        link_url: submissionData.linkUrl || null,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        is_late: submissionData.isLate || false
      })
      .eq('id', submissionId)
      .select()
      .single();
    return { data, error };
  },

  // Grade a submission (admin)
  async gradeSubmission(submissionId, gradeData) {
    const { data, error } = await supabase
      .from('assignment_submissions')
      .update({
        status: gradeData.status || 'graded',
        grade: gradeData.grade,
        points_earned: gradeData.pointsEarned,
        feedback: gradeData.feedback,
        reviewed_at: new Date().toISOString(),
        reviewed_by: gradeData.reviewedBy
      })
      .eq('id', submissionId)
      .select()
      .single();
    return { data, error };
  },

  // Delete a submission
  async deleteSubmission(submissionId) {
    const { error } = await supabase
      .from('assignment_submissions')
      .delete()
      .eq('id', submissionId);
    return { error };
  },

  // Upload submission file to storage
  async uploadSubmissionFile(studentId, moduleItemId, file) {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${studentId}/${moduleItemId}/${Date.now()}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('submissions')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (error) throw error;

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('submissions')
        .getPublicUrl(fileName);

      return { 
        data: {
          path: data.path,
          url: urlData.publicUrl,
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          fileType: file.type
        }, 
        error: null 
      };
    } catch (err) {
      console.error('Upload error:', err);
      return { data: null, error: err };
    }
  },

  // =====================================================
  // MULTI-FILE SUBMISSION SUPPORT
  // =====================================================

  // Upload multiple files for a submission
  async uploadSubmissionFiles(studentId, moduleItemId, files) {
    const results = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${studentId}/${moduleItemId}/${Date.now()}_${i}.${fileExt}`;

        const { data, error } = await supabase.storage
          .from('submissions')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: true
          });

        if (error) throw error;

        const { data: urlData } = supabase.storage
          .from('submissions')
          .getPublicUrl(fileName);

        results.push({
          path: data.path,
          url: urlData.publicUrl,
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          fileType: file.type,
          sortOrder: i
        });
      } catch (err) {
        console.error('Upload error for file:', file.name, err);
        return { data: null, error: err };
      }
    }
    return { data: results, error: null };
  },

  // Save file records to the submission_files table
  async saveSubmissionFiles(submissionId, fileRecords) {
    // First remove any existing files for this submission
    await supabase
      .from('submission_files')
      .delete()
      .eq('submission_id', submissionId);

    if (!fileRecords || fileRecords.length === 0) {
      return { data: [], error: null };
    }

    const rows = fileRecords.map((f, i) => ({
      submission_id: submissionId,
      file_url: f.url,
      file_name: f.fileName,
      file_size: f.fileSize || null,
      file_type: f.fileType || null,
      storage_path: f.path || null,
      sort_order: f.sortOrder ?? i
    }));

    const { data, error } = await supabase
      .from('submission_files')
      .insert(rows)
      .select();
    return { data, error };
  },

  // Get all files for a submission
  async getSubmissionFiles(submissionId) {
    const { data, error } = await supabase
      .from('submission_files')
      .select('*')
      .eq('submission_id', submissionId)
      .order('sort_order', { ascending: true });
    return { data: data || [], error };
  },

  // Get the attached files for many submissions in one round trip.
  // Used by the submissions PDF export so we don't fire one query per student.
  async getSubmissionFilesForSubmissions(submissionIds) {
    if (!submissionIds || submissionIds.length === 0) return { data: [], error: null };
    const { data, error } = await supabase
      .from('submission_files')
      .select('*')
      .in('submission_id', submissionIds)
      .order('sort_order', { ascending: true });
    return { data: data || [], error };
  },

  // Delete a single submission file
  async deleteSubmissionFile(fileId) {
    const { error } = await supabase
      .from('submission_files')
      .delete()
      .eq('id', fileId);
    return { error };
  },

  // =====================================================
  // ATTENDANCE
  // =====================================================

  // Get all attendance sessions for a cohort
  async getAttendanceSessions(cohortId) {
    const { data, error } = await supabase
      .from('attendance_sessions')
      .select('*')
      .eq('cohort_id', cohortId)
      .order('session_date', { ascending: true });
    return { data, error };
  },

  // Create a new attendance session
  async createAttendanceSession(sessionData) {
    const { data, error } = await supabase
      .from('attendance_sessions')
      .insert({
        cohort_id: sessionData.cohortId,
        title: sessionData.title,
        session_type: sessionData.sessionType,
        session_date: sessionData.sessionDate,
        start_time: sessionData.startTime || null,
        end_time: sessionData.endTime || null,
        notes: sessionData.notes || null,
        created_by: sessionData.createdBy
      })
      .select()
      .single();
    return { data, error };
  },

  // Update an attendance session
  async updateAttendanceSession(sessionId, updates) {
    const { data, error } = await supabase
      .from('attendance_sessions')
      .update({
        title: updates.title,
        session_type: updates.sessionType,
        session_date: updates.sessionDate,
        start_time: updates.startTime || null,
        end_time: updates.endTime || null,
        notes: updates.notes || null
      })
      .eq('id', sessionId)
      .select()
      .single();
    return { data, error };
  },

  // Delete an attendance session (cascades to records)
  async deleteAttendanceSession(sessionId) {
    const { error } = await supabase
      .from('attendance_sessions')
      .delete()
      .eq('id', sessionId);
    return { error };
  },

  // Get all attendance records for a session
  async getAttendanceRecords(sessionId) {
    const { data, error } = await supabase
      .from('attendance_records')
      .select(`
        *,
        student:student_id(id, name, email)
      `)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    return { data, error };
  },

  // Bulk upsert attendance records for a session
  async saveAttendanceRecords(sessionId, records, recordedBy) {
    const upsertData = records.map(r => ({
      session_id: sessionId,
      student_id: r.studentId,
      status: r.status,
      check_in_time: r.status === 'present' || r.status === 'late' || r.status === 'left-early' ? new Date().toISOString() : null,
      notes: r.notes || null,
      recorded_by: recordedBy
    }));

    const { data, error } = await supabase
      .from('attendance_records')
      .upsert(upsertData, { onConflict: 'session_id,student_id' })
      .select(`
        *,
        student:student_id(id, name, email)
      `);
    return { data, error };
  },

  // Get attendance summary for a student across all sessions in a cohort
  async getStudentAttendanceSummary(studentId, cohortId) {
    const { data: sessions } = await supabase
      .from('attendance_sessions')
      .select('id')
      .eq('cohort_id', cohortId);

    if (!sessions || sessions.length === 0) {
      return { data: { total: 0, present: 0, absent: 0, late: 0, excused: 0, 'left-early': 0, other: 0 }, error: null };
    }

    const sessionIds = sessions.map(s => s.id);
    const { data: records, error } = await supabase
      .from('attendance_records')
      .select('status')
      .eq('student_id', studentId)
      .in('session_id', sessionIds);

    if (error) return { data: null, error };

    const summary = {
      total: sessions.length,
      present: records?.filter(r => r.status === 'present').length || 0,
      late: records?.filter(r => r.status === 'late').length || 0,
      excused: records?.filter(r => r.status === 'excused').length || 0,
      'left-early': records?.filter(r => r.status === 'left-early').length || 0,
      other: records?.filter(r => r.status === 'other').length || 0,
      absent: 0
    };
    // Absent = total sessions minus those with any recorded non-absent status
    const accounted = summary.present + summary.late + summary.excused + summary['left-early'] + summary.other;
    summary.absent = Math.max(0, summary.total - accounted);

    return { data: summary, error: null };
  },

  // =====================================================
  // ELIGIBILITY DATA
  // =====================================================

  // Get all attendance records for all students in a cohort (for eligibility calculation)
  async getCohortAttendanceRecords(cohortId) {
    // Get all sessions for the cohort
    const { data: sessions, error: sessErr } = await supabase
      .from('attendance_sessions')
      .select('id, session_date, title')
      .eq('cohort_id', cohortId)
      .order('session_date', { ascending: true });

    if (sessErr || !sessions) return { data: { sessions: [], records: [] }, error: sessErr };

    const sessionIds = sessions.map(s => s.id);
    if (sessionIds.length === 0) return { data: { sessions, records: [] }, error: null };

    const { data: records, error: recErr } = await supabase
      .from('attendance_records')
      .select('id, session_id, student_id, status')
      .in('session_id', sessionIds);

    return { data: { sessions, records: records || [] }, error: recErr };
  },

  // Get all submissions for a cohort (simplified for eligibility)
  async getCohortSubmissionsForEligibility(cohortId) {
    const { data: students } = await supabase
      .from('profiles')
      .select('id')
      .eq('cohort_id', cohortId)
      .eq('role', 'student');

    if (!students || students.length === 0) return { data: [], error: null };

    const studentIds = students.map(s => s.id);

    const { data, error } = await supabase
      .from('assignment_submissions')
      .select('id, student_id, module_item_id, status, is_late, submitted_at')
      .in('student_id', studentIds);

    return { data: data || [], error };
  },

  // =====================================================
  // REFLECTION QUESTIONS & RESPONSES
  // =====================================================

  // Get reflection questions for a module
  async getReflectionQuestions(moduleId) {
    const { data, error } = await supabase
      .from('reflection_questions')
      .select('*')
      .eq('module_id', moduleId)
      .order('question_order', { ascending: true });
    return { data, error };
  },

  // Get all reflection questions (for all modules)
  async getAllReflectionQuestions() {
    const { data, error } = await supabase
      .from('reflection_questions')
      .select('*')
      .order('question_order', { ascending: true });
    return { data, error };
  },

  // Create a reflection question
  async createReflectionQuestion(questionData) {
    const { data, error } = await supabase
      .from('reflection_questions')
      .insert({
        module_id: questionData.moduleId,
        question_text: questionData.questionText,
        question_order: questionData.questionOrder || 1,
        created_by: questionData.createdBy
      })
      .select()
      .single();
    return { data, error };
  },

  // Update a reflection question
  async updateReflectionQuestion(questionId, updates) {
    const { data, error } = await supabase
      .from('reflection_questions')
      .update({
        question_text: updates.questionText,
        question_order: updates.questionOrder
      })
      .eq('id', questionId)
      .select()
      .single();
    return { data, error };
  },

  // Delete a reflection question
  async deleteReflectionQuestion(questionId) {
    const { error } = await supabase
      .from('reflection_questions')
      .delete()
      .eq('id', questionId);
    return { error };
  },

  // Get reflection responses for a student
  async getStudentReflectionResponses(studentId) {
    const { data, error } = await supabase
      .from('reflection_responses')
      .select('*')
      .eq('student_id', studentId);
    return { data, error };
  },

  // Get all reflection responses for a cohort (admin)
  async getCohortReflectionResponses(cohortId) {
    const { data: students } = await supabase
      .from('profiles')
      .select('id')
      .eq('cohort_id', cohortId)
      .eq('role', 'student');

    if (!students || students.length === 0) return { data: [], error: null };
    const studentIds = students.map(s => s.id);

    const { data, error } = await supabase
      .from('reflection_responses')
      .select('*')
      .in('student_id', studentIds);
    return { data: data || [], error };
  },

  // Upsert a reflection response (student saves/updates their answer)
  async upsertReflectionResponse(responseData) {
    const { data, error } = await supabase
      .from('reflection_responses')
      .upsert({
        question_id: responseData.questionId,
        student_id: responseData.studentId,
        response_text: responseData.responseText,
        submitted_at: new Date().toISOString()
      }, { onConflict: 'question_id,student_id' })
      .select()
      .single();
    return { data, error };
  },

  // =====================================================
  // MESSAGES
  // =====================================================

  // Get all messages for a user (inbox + sent)
  async getUserMessages(userId) {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey(id, name, email, avatar_url, role),
        recipient:profiles!messages_recipient_id_fkey(id, name, email, avatar_url, role)
      `)
      .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Get inbox messages (received) - includes replies
  async getInboxMessages(userId) {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey(id, name, email, avatar_url, role)
      `)
      .eq('recipient_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Get sent messages - includes replies
  async getSentMessages(userId) {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        recipient:profiles!messages_recipient_id_fkey(id, name, email, avatar_url, role)
      `)
      .eq('sender_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  // Get conversation thread (message + replies)
  async getConversation(messageId, userId) {
    const { data, error } = await supabase
      .from('messages')
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey(id, name, email, avatar_url, role),
        recipient:profiles!messages_recipient_id_fkey(id, name, email, avatar_url, role)
      `)
      .or(`id.eq.${messageId},parent_id.eq.${messageId}`)
      .order('created_at', { ascending: true });
    return { data, error };
  },

  // Send a new message
  async sendMessage(senderId, recipientId, subject, body, parentId = null) {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        sender_id: senderId,
        recipient_id: recipientId,
        subject: subject,
        body: body,
        parent_id: parentId
      })
      .select(`
        *,
        sender:profiles!messages_sender_id_fkey(id, name, email, avatar_url, role),
        recipient:profiles!messages_recipient_id_fkey(id, name, email, avatar_url, role)
      `)
      .single();
    return { data, error };
  },

  // Mark message as read
  async markMessageRead(messageId) {
    const { data, error } = await supabase
      .from('messages')
      .update({ 
        is_read: true, 
        read_at: new Date().toISOString() 
      })
      .eq('id', messageId)
      .select()
      .single();
    return { data, error };
  },

  // Get unread message count
  async getUnreadCount(userId) {
    const { count, error } = await supabase
      .from('messages')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_id', userId)
      .eq('is_read', false);
    return { count, error };
  },

  // =====================================================
  // SETTINGS
  // =====================================================

  // Get all settings
  async getSettings() {
    const { data, error } = await supabase
      .from('settings')
      .select('*');
    return { data, error };
  },

  // Get a specific setting by key
  async getSetting(key) {
    const { data, error } = await supabase
      .from('settings')
      .select('*')
      .eq('key', key)
      .single();
    return { data, error };
  },

  // Update a setting
  async updateSetting(key, value, userId) {
    const { data, error } = await supabase
      .from('settings')
      .update({ 
        value, 
        updated_at: new Date().toISOString(),
        updated_by: userId
      })
      .eq('key', key)
      .select()
      .single();
    return { data, error };
  },

  // =====================================================
  // THINKIFIC UNLINKED ENROLLMENTS (read-only from the client)
  // -----------------------------------------------------------------
  // Tracks Thinkific NCLEX students who don't have an LMS profile.
  // Populated by sync and webhooks. Admin-only via RLS.
  // =====================================================
  async getThinkificUnlinkedEnrollments() {
    const { data, error } = await supabase
      .from('thinkific_unlinked_enrollments')
      .select('*')
      .order('email', { ascending: true });
    return { data, error };
  },

  // =====================================================
  // THINKIFIC AUDIT LOG (read-only from the client)
  // =====================================================
  async getThinkificAuditLog({ limit = 100, studentId = null } = {}) {
    let query = supabase
      .from('thinkific_audit_log')
      .select('*, actor:actor_id(name, email), student:student_id(name, email)')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (studentId) query = query.eq('student_id', studentId);
    const { data, error } = await query;
    return { data, error };
  },

  // Upsert a setting (create if doesn't exist)
  async upsertSetting(key, value, description, userId) {
    const { data, error } = await supabase
      .from('settings')
      .upsert({ 
        key,
        value, 
        description,
        updated_at: new Date().toISOString(),
        updated_by: userId
      })
      .select()
      .single();
    return { data, error };
  }
};

// Helper function to format file size
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export default supabase;
